import React from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ui } from '../../config/uiSizes';
import { useAuth } from '../../hooks/auth';
import { useSmartBack } from '../../hooks/ui';
import { startRollback } from '../../services/Rollback/rollbackMigrationService';
import { fetchSnapshotComparison } from '../../services/SnapshotComparison/fetchSnapshotComparisonService';
import type { Migration } from '../../types/migration';
import type { SnapshotComparisonResult } from '../../types/snapshotComparison';
import { formatUtcTimestampLocal } from '../../utils/formatters';
import Spinner from '../../components/migratorDashboard/Spinner';
import SnapshotComparisonView from '../../components/SnapshotComparison/SnapshotComparisonView';
import { logger } from '../../utils/logger';

// ── Constants ─────────────────────────────────────────────────────────────────

const ROLLBACK_WINDOW_DAYS = 5;

// ── Banner helpers ─────────────────────────────────────────────────────────────

function computeDaysSince(isoUtc: string | null | undefined): number | null {
    if (!isoUtc) return null;
    return Math.floor((Date.now() - new Date(isoUtc).getTime()) / (1000 * 60 * 60 * 24));
}

interface BannerConfig {
    variant: 'green' | 'amber' | 'red';
    message: string;
}

function buildBannerConfig(days: number): BannerConfig {
    const daysRemaining = Math.max(0, ROLLBACK_WINDOW_DAYS - days);
    const dayStr = (n: number) => `${n} day${n !== 1 ? 's' : ''}`;

    if (days <= 2) {
        return {
            variant: 'green',
            message: `${dayStr(days)} since migration completion — environment changes are unlikely. Automated rollback available for ${dayStr(daysRemaining)} more.`,
        };
    }
    if (days <= 4) {
        return {
            variant: 'amber',
            message: `${dayStr(days)} since migration completion — your environment may have changed since this migration ran. Automated rollback available for ${dayStr(daysRemaining)} more.`,
        };
    }
    if (days === ROLLBACK_WINDOW_DAYS) {
        return {
            variant: 'red',
            message: '5 days since migration completion — significant time has passed. Manual review is strongly recommended before proceeding. Automated rollback will no longer be available after today.',
        };
    }
    return {
        variant: 'red',
        message: `${dayStr(days)} since migration completion — automated rollback is no longer available.`,
    };
}



// ── Page ──────────────────────────────────────────────────────────────────────

const RollbackInitiationPage: React.FC = () => {
    const navigate = useNavigate();
    const smartBack = useSmartBack();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { accessToken } = useAuth();

    const migrationId = searchParams.get('migrationId') ?? '';
    const job = (location.state as { job?: Migration } | null)?.job ?? null;

    // ── Time-elapsed banner ────────────────────────────────────────────────────

    const completedAt = job?.job3Import?.completedAtUtc ?? job?.updatedAtUtc ?? null;

    const [daysSinceCompletion, setDaysSinceCompletion] = React.useState<number | null>(() =>
        computeDaysSince(completedAt)
    );

    React.useEffect(() => {
        if (!completedAt) return;
        const id = setInterval(() => {
            setDaysSinceCompletion(computeDaysSince(completedAt));
        }, 60_000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completedAt]);

    const bannerConfig: BannerConfig | null =
        daysSinceCompletion !== null ? buildBannerConfig(daysSinceCompletion) : null;

    // ── Comparison fetch ──────────────────────────────────────────────────────

    const [comparisonStatus, setComparisonStatus] = React.useState<'loading' | 'ready' | 'error'>('loading');
    const [comparison, setComparison] = React.useState<SnapshotComparisonResult | null>(null);
    const [comparisonError, setComparisonError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!migrationId || !accessToken) return;
        let cancelled = false;

        const load = async () => {
            try {
                setComparisonStatus('loading');
                const data = await fetchSnapshotComparison(accessToken, migrationId, undefined, undefined, true);
                if (cancelled) return;
                setComparison(data);
                setComparisonStatus('ready');
            } catch (e) {
                if (cancelled) return;
                const msg = e instanceof Error ? e.message : 'Failed to load resource comparison.';
                setComparisonError(msg);
                setComparisonStatus('error');
                logger.error('Error fetching rollback comparison', { migrationId, error: msg });
            }
        };

        load();
        return () => { cancelled = true; };
    }, [migrationId, accessToken]);

    // ── Initiate rollback ──────────────────────────────────────────────────────

    const [initiating, setInitiating] = React.useState(false);
    const [initiateError, setInitiateError] = React.useState<string | null>(null);

    const handleInitiate = React.useCallback(async () => {
        if (!accessToken) return;
        try {
            setInitiating(true);
            setInitiateError(null);
            await startRollback(accessToken, migrationId);
            navigate('/dashboard');
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Failed to initiate rollback.';
            setInitiateError(msg);
            setInitiating(false);
            logger.error('Error initiating rollback', { migrationId, error: msg });
        }
    }, [accessToken, migrationId, navigate]);

    // ── Derived flags ──────────────────────────────────────────────────────────

    const isExpired =
        daysSinceCompletion !== null && daysSinceCompletion > ROLLBACK_WINDOW_DAYS;

    const hasChanges =
        comparison !== null &&
        (comparison.summary.instancesAdded + comparison.summary.instancesModified + comparison.summary.instancesDeleted) > 0;

    const canInitiate =
        !isExpired &&
        !initiating &&
        comparisonStatus === 'ready' &&
        comparison !== null &&
        hasChanges;

    const formattedTimestamp = completedAt ? formatUtcTimestampLocal(completedAt) : '—';

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="px-6 py-6 xl:py-5 lg:py-4 md:py-3 md:px-4 lg:px-5" style={{ maxWidth: '1536px', marginLeft: 'auto', marginRight: 'auto' }}>
            <div className="flex flex-col rounded-2xl border border-gray-300 bg-gray-200 shadow-xl dark:border-gray-700 dark:bg-gray-800 overflow-hidden mb-2 p-1">
                <div className={`flex flex-col flex-1 rounded-2xl border border-gray-200 bg-gray-100 shadow-xl dark:border-gray-600 dark:bg-gray-700 overflow-hidden m-2 ${ui.page.cardPadding}`}>

                    {/* ── Top info card ───────────────────────────────────── */}
                    <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 ${ui.page.cardPadding} ${ui.mb.section}`}>

                        {/* Title */}
                        <div className={`flex items-start ${ui.mb.sm}`}>
                            <div className="inline-flex flex-col items-start">
                                <p className={`${ui.text.xs} font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1`}>
                                    Migration Name
                                </p>
                                <h2 className={`${ui.text.pageTitle} font-extrabold leading-none tracking-tight text-gray-800 dark:text-gray-100`}>
                                    {job?.name ?? migrationId}
                                </h2>
                                <div aria-hidden="true" className="mt-2 h-1 w-16 bg-blue-600 dark:bg-blue-500 rounded" />
                            </div>
                        </div>

                        {/* Description */}
                        <p className={`${ui.text.body} text-gray-600 dark:text-gray-300 ${ui.mb.section}`}>
                            Review the affected resources below, then click <strong className="font-semibold text-gray-800 dark:text-gray-200">Initiate Rollback</strong> to begin.
                            No changes will be made to your organization until you review and approve a plan on the next screen.
                        </p>

                        {/* Metadata grid */}
                        <div className={`grid grid-cols-2 ${ui.gap.md}`}>
                            {/* Completion timestamp */}
                            <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3">
                                <div className="flex-shrink-0 rounded-md bg-purple-100 dark:bg-purple-900/40 p-2">
                                    <svg className={`${ui.svg.sm} text-purple-600 dark:text-purple-400`} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <p className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide`}>
                                        Migration Completed
                                    </p>
                                    <p className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100 truncate`} title={formattedTimestamp}>
                                        {formattedTimestamp}
                                    </p>
                                </div>
                            </div>
                            {/* Target org */}
                            <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3">
                                <div className="flex-shrink-0 rounded-md bg-emerald-100 dark:bg-emerald-900/40 p-2">
                                    <svg className={`${ui.svg.sm} text-emerald-600 dark:text-emerald-400`} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4zm7 5a1 1 0 10-2 0v1H8a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <p className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide`}>
                                        Target Organization
                                    </p>
                                    <p className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100 truncate`} title={job?.targetOrgName ?? '—'}>
                                        {job?.targetOrgName ?? '—'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Time-elapsed banner */}
                        {bannerConfig && (
                            <div className="mt-4 pt-1">
                                <div className={`flex items-start ${ui.gap.sm} rounded-xl px-4 py-3 border ${ui.mb.title} ${bannerConfig.variant === 'green'
                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                    : bannerConfig.variant === 'amber'
                                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                                        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                                    }`}>
                                    <svg
                                        className={`flex-shrink-0 mt-0.5 ${ui.svg.sm} ${bannerConfig.variant === 'green'
                                            ? 'text-green-600 dark:text-green-400'
                                            : bannerConfig.variant === 'amber'
                                                ? 'text-amber-600 dark:text-amber-400'
                                                : 'text-red-600 dark:text-red-400'
                                            }`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        strokeWidth={2}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <p className={`${ui.text.body} font-semibold ${bannerConfig.variant === 'green'
                                        ? 'text-green-800 dark:text-green-200'
                                        : bannerConfig.variant === 'amber'
                                            ? 'text-amber-800 dark:text-amber-200'
                                            : 'text-red-800 dark:text-red-200'
                                        }`}>
                                        {bannerConfig.message}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* ── What happens next ───────────────────────── */}
                        <div className="mt-5 pt-5 border-t border-gray-100 dark:border-gray-700">
                            <p className={`${ui.text.xs} font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4`}>
                                What happens when you click Initiate Rollback
                            </p>
                            <ol className="flex flex-col">
                                {([
                                    {
                                        n: 1,
                                        title: 'Plan generation',
                                        body: 'RepliCloud analyses your snapshot and calculates every resource change. This runs in the background — when it is ready, the plan will appear in your dashboard for review.',
                                        tag: 'Runs in background',
                                        color: 'blue' as const,
                                    },
                                    {
                                        n: 2,
                                        title: 'Your review',
                                        body: 'You will be notified when the plan is ready. You can review every resource change in detail before anything is applied.',
                                        tag: 'Your action required',
                                        color: 'blue' as const,
                                    },
                                    {
                                        n: 3,
                                        title: 'Automated rollback',
                                        body: 'Once you approve the plan, the rollback runs automatically in the background. No further action is needed.',
                                        tag: null,
                                        color: 'green' as const,
                                    },
                                ] as const).map((step, idx, arr) => (
                                    <li key={step.n} className="flex gap-4">
                                        {/* Number circle + connector */}
                                        <div className="flex flex-col items-center shrink-0">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.color === 'green' ? 'bg-green-100 dark:bg-green-900/40' : 'bg-blue-100 dark:bg-blue-900/40'}`}>
                                                <span className={`${ui.text.xs} font-bold ${step.color === 'green' ? 'text-green-700 dark:text-green-300' : 'text-blue-700 dark:text-blue-300'}`}>
                                                    {step.n}
                                                </span>
                                            </div>
                                            {idx < arr.length - 1 && (
                                                <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 my-1" />
                                            )}
                                        </div>
                                        {/* Content */}
                                        <div className={`${idx < arr.length - 1 ? 'pb-4' : ''} min-w-0 flex-1`}>
                                            <div className={`flex items-center ${ui.gap.sm} mb-0.5`}>
                                                <p className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100`}>
                                                    {step.title}
                                                </p>
                                                {step.tag && (
                                                    <span className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400`}>
                                                        {step.tag}
                                                    </span>
                                                )}
                                            </div>
                                            <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400`}>
                                                {step.body}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    {/* ── Resources affected card ────────────────────────── */}
                    <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 ${ui.page.cardPadding}`}>

                        {/* Header row */}
                        <div className={`flex items-center justify-between ${ui.mb.title}`}>
                            <div className="flex items-center gap-2">
                                {comparisonStatus === 'ready' && comparison && (
                                    <span className={`${ui.text.xs} font-bold text-white bg-blue-600 dark:bg-blue-700 px-2.5 py-0.5 rounded-full shrink-0`}>
                                        {comparison.summary.instancesAdded + comparison.summary.instancesModified + comparison.summary.instancesDeleted}
                                    </span>
                                )}
                                <h3 className={`${ui.text.sectionTitle} font-bold text-gray-800 dark:text-gray-100`}>
                                    Resources that will be Affected by Rollback
                                </h3>
                            </div>
                            {comparisonStatus === 'ready' && comparison && (
                                <div className="flex items-center gap-3">
                                    <span className={`${ui.text.xs} flex items-center gap-1 font-medium text-green-700 dark:text-green-400`}>
                                        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Added
                                    </span>
                                    <span className={`${ui.text.xs} flex items-center gap-1 font-medium text-orange-600 dark:text-orange-400`}>
                                        <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" /> Modified
                                    </span>
                                    <span className={`${ui.text.xs} flex items-center gap-1 font-medium text-red-600 dark:text-red-400`}>
                                        <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Removed
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Loading */}
                        {comparisonStatus === 'loading' && (
                            <div className="flex items-center justify-center gap-3 py-8">
                                <Spinner />
                                <span className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                                    Checking what this rollback will affect…
                                </span>
                            </div>
                        )}

                        {/* Error */}
                        {comparisonStatus === 'error' && (
                            <div className={`rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 ${ui.text.xs} text-red-800 dark:text-red-200`}>
                                {comparisonError ?? 'Failed to load resource comparison.'}
                            </div>
                        )}

                        {/* Ready — full diff view */}
                        {comparisonStatus === 'ready' && comparison && (
                            <SnapshotComparisonView resourceTypes={comparison.resourceTypes} />
                        )}
                    </div>

                    {/* ── Initiate error + action buttons ─────────────────── */}
                    <div className={`flex flex-col ${ui.gap.md} mt-6`}>
                        {initiateError && (
                            <div className={`rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 ${ui.text.xs} text-red-800 dark:text-red-200`}>
                                {initiateError}
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <button
                                type="button"
                                onClick={smartBack}
                                disabled={initiating}
                                className={`${ui.button.md} rounded-lg font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 transition-colors`}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleInitiate}
                                disabled={!canInitiate}
                                className={`${ui.button.lg} rounded-xl font-semibold flex items-center ${ui.gap.sm} transition-colors ${isExpired || comparison === null || !hasChanges
                                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                    : 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white disabled:opacity-50'
                                    }`}
                            >
                                {initiating ? (
                                    <>
                                        <svg className={`animate-spin ${ui.svg.sm} text-white`} viewBox="0 0 24 24" fill="none">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                        </svg>
                                        Initiating…
                                    </>
                                ) : (
                                    <>
                                        Initiate Rollback
                                        <svg className={ui.svg.sm} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default RollbackInitiationPage;
